// Single source of truth for the Solutions mega-menu, the /solutions index,
// and every /solutions/:slug page. Each entry powers SEO meta + page body.
//
// The shared moat we surface on EVERY solution page: natural-language triggers.
// Tell Zenstream what to detect or remove in plain English — it reasons over
// images, audio, video, and text, then acts.

export const SOLUTION_CATEGORIES = [
  "Technology & Digital Platforms",
  "Media & Sports",
  "Risk & Identity Management",
  "Use Cases",
  "API",
];

export const SOLUTIONS = [
  // ── Technology & Digital Platforms ──
  {
    slug: "online-communities",
    category: "Technology & Digital Platforms",
    title: "For Online Communities",
    short:
      "Screen user-generated visual, text, and audio content in real-time.",
    headline: "Real-time moderation for online communities",
    subheadline:
      "Catch hate-speech, NSFW media, doxxing, and spam the second it lands. Zenstream's natural-language triggers let your trust & safety team write rules in English — no taxonomy gymnastics.",
    perks: [
      {
        title: "Multimodal in one pipeline",
        body: "Text, image, video, and audio scored against the same rule — you don't have to stitch four vendors together.",
      },
      {
        title: "Tunable per-community",
        body: "Different subreddits, servers, or rooms can run different policies. Zenstream applies the right rule based on context.",
      },
      {
        title: "Reasoning, not regex",
        body: "Triggers understand intent. \"Hide attacks on a user's appearance\" works without a hand-curated word list.",
      },
    ],
    triggerExamples: [
      "Hide any post that doxes a real person, even if the address is paraphrased.",
      "Auto-collapse comments that escalate toward harassment.",
      "Block uploaded images that depict self-harm and route to a human reviewer.",
    ],
    seoKeywords:
      "online community moderation, UGC moderation, AI trust and safety, real-time content moderation, forum moderation API, multimodal moderation",
  },
  {
    slug: "streaming-platforms",
    category: "Technology & Digital Platforms",
    title: "For Streaming Platforms",
    short:
      "Monitor live streams for harmful content, brand safety, and real-time insights.",
    headline: "Live-stream moderation that thinks for itself",
    subheadline:
      "Zenstream watches every frame and word of a live broadcast and acts in under 250 ms. Connect once via OBS WebSocket or our ingest API — every channel, every platform.",
    perks: [
      {
        title: "Sub-250 ms detection-to-action",
        body: "Cut audio, blur frames, or kill the stream before viewers see it.",
      },
      {
        title: "Per-channel policies",
        body: "Family-friendly channels and 18+ channels share the same engine but enforce different rules.",
      },
      {
        title: "Sponsor-grade audit log",
        body: "Every fired trigger is timestamped, exportable, and tied to its natural-language source rule.",
      },
    ],
    triggerExamples: [
      "Mute the broadcast for 4 seconds if the host says a slur.",
      "Blur the camera if a real face appears that isn't on the approved roster.",
      "Alert the on-call team if chat sentiment turns toxic for 30+ seconds.",
    ],
    seoKeywords:
      "live stream moderation, real-time stream censoring, OBS moderation, streaming platform safety, live video AI moderation, broadcast compliance",
  },
  {
    slug: "marketplaces",
    category: "Technology & Digital Platforms",
    title: "For Marketplaces",
    short:
      "Verify listings, flag spam, and keep marketplace interactions safe.",
    headline: "Listing-quality and trust at marketplace scale",
    subheadline:
      "Zenstream reviews every listing image, title, and message in milliseconds. Catch counterfeits, recalled products, and scammy DMs before a buyer ever sees them.",
    perks: [
      {
        title: "Listing photo screening",
        body: "Detect stolen photos, watermarks, weapons, and prohibited categories at upload.",
      },
      {
        title: "Message-thread protection",
        body: "Flag off-platform redirects, payment scams, and grooming patterns in DMs.",
      },
      {
        title: "Policy in plain English",
        body: "\"Reject listings that look like recalled cribs\" runs the same day you write it.",
      },
    ],
    triggerExamples: [
      "Reject any listing photo containing a firearm, ammunition, or holster.",
      "Flag DMs that ask the buyer to pay via gift card or wire transfer.",
      "Auto-hide listings whose photo was lifted from a known stock site.",
    ],
    seoKeywords:
      "marketplace moderation, listing image review, scam detection, counterfeit detection, P2P safety, e-commerce trust and safety",
  },
  {
    slug: "generative-ai-apps",
    category: "Technology & Digital Platforms",
    title: "For Generative AI Apps",
    short:
      "Protect users from NSFW, hateful, CSAM, and IP-violating content in AI generations.",
    headline: "Safety rails for generative AI apps",
    subheadline:
      "Wrap any image, video, or audio model with Zenstream. Enforce CSAM zero-tolerance, brand IP, and product-specific rules — written in English, applied per call.",
    perks: [
      {
        title: "Zero-tolerance CSAM filter",
        body: "Industry-grade hash + ML detection on every output, with audit trail.",
      },
      {
        title: "Per-tenant policies",
        body: "Each customer of your AI app gets their own rule set without spinning up new infra.",
      },
      {
        title: "Output-side reasoning",
        body: "Block outputs that look like a public figure even if the prompt didn't name them.",
      },
    ],
    triggerExamples: [
      "Reject any generation that resembles a recognizable celebrity.",
      "Block outputs that depict minors in any context.",
      "Watermark and log every photoreal human face this app creates.",
    ],
    seoKeywords:
      "generative AI safety, image model moderation, CSAM detection API, AI safety filter, output moderation, gen AI guardrails",
  },
  {
    slug: "gaming-companies",
    category: "Technology & Digital Platforms",
    title: "For Gaming Companies",
    short:
      "Protect in-game chats and forums from toxicity, scams, and NSFW content.",
    headline: "In-game safety without a 200-person mod team",
    subheadline:
      "Voice chat, text chat, user-generated maps, and Discord-style forums — all moderated against your community guidelines, all written in English.",
    perks: [
      {
        title: "Voice + text in one model",
        body: "The same trigger catches typed and spoken slurs without two parallel pipelines.",
      },
      {
        title: "Cheating & scam detection",
        body: "Flag account-trade DMs, RMT solicitations, and phishing links automatically.",
      },
      {
        title: "Player-reputation scoring",
        body: "Aggregate fired triggers into a reputation that drives shadowbans, queue priority, or matchmaking.",
      },
    ],
    triggerExamples: [
      "Mute voice chat for any player who uses a slur in two consecutive matches.",
      "Flag user-built levels that contain swastikas or hate imagery.",
      "Block DMs that solicit account trades or real-money transfers.",
    ],
    seoKeywords:
      "game chat moderation, voice chat moderation, gaming trust and safety, anti-toxicity AI, in-game scam detection",
  },
  {
    slug: "dating-apps",
    category: "Technology & Digital Platforms",
    title: "For Dating Apps",
    short:
      "Elevate user experience with better moderation and user verification.",
    headline: "Safer dating, on by default",
    subheadline:
      "Zenstream catches catfishing, deepfaked profile photos, unsolicited explicit images, and sextortion patterns — then explains *why* every action fired.",
    perks: [
      {
        title: "Selfie-vs-profile match",
        body: "Liveness + identity matching that ships in days, not quarters.",
      },
      {
        title: "Unsolicited NSFW shield",
        body: "Inbound explicit images get blurred until the receiver consents to view.",
      },
      {
        title: "Sextortion pattern detection",
        body: "Stop the conversation flow that escalates from flirting to financial coercion.",
      },
    ],
    triggerExamples: [
      "Auto-blur any explicit image in DMs until the recipient taps to reveal.",
      "Flag profiles whose photos appear on known model-image scraping sites.",
      "Pause conversations where one user asks for money in the first 48 hours.",
    ],
    seoKeywords:
      "dating app moderation, catfish detection, NSFW DM filter, sextortion detection, online dating safety AI",
  },
  {
    slug: "rideshare-delivery",
    category: "Technology & Digital Platforms",
    title: "For Rideshare and Delivery Services",
    short:
      "Detect potential fraud, protect users and drivers, and automate operations.",
    headline: "Trust, safety, and ops automation for rideshare & delivery",
    subheadline:
      "Verify driver selfies, score in-cabin audio for harassment, screen photo proofs of delivery — each policy written by ops, in English, in minutes.",
    perks: [
      {
        title: "Driver liveness & identity",
        body: "Pre-shift selfie checks that detect photo-of-photo, masks, and deepfakes.",
      },
      {
        title: "Photo proof verification",
        body: "Confirm a delivery photo actually shows a package, not a stock image.",
      },
      {
        title: "In-cabin audio scoring (opt-in)",
        body: "Detect verbal aggression in real time and route to safety operations.",
      },
    ],
    triggerExamples: [
      "Reject delivery proof photos that don't contain a visible package.",
      "Page the safety team if in-cabin audio contains escalating aggression.",
      "Block drivers from going online if their selfie doesn't match their profile.",
    ],
    seoKeywords:
      "rideshare safety AI, driver identity verification, delivery proof verification, gig economy fraud detection",
  },
  {
    slug: "music-companies",
    category: "Technology & Digital Platforms",
    title: "For Music Companies",
    short:
      "Detect AI-generated content and manage audio, lyrics, and visuals effectively.",
    headline: "Catalog protection for the streaming era",
    subheadline:
      "Spot AI-generated soundalikes of your artists, screen lyric-video uploads for slurs, and police cover art — all from one English-language policy.",
    perks: [
      {
        title: "AI-soundalike detection",
        body: "Identify uploads that mimic an artist's voice without the rights to do so.",
      },
      {
        title: "Lyric & cover-art screening",
        body: "Block uploads where lyrics or cover art breach distribution policy.",
      },
      {
        title: "Catalog-wide search",
        body: "\"Find every track in our catalog whose cover depicts a firearm.\"",
      },
    ],
    triggerExamples: [
      "Flag uploads whose vocals match the timbre profile of one of our artists.",
      "Reject cover art that contains a real person's face we don't have rights to.",
      "Auto-tag tracks whose lyrics contain a slur for explicit-flag review.",
    ],
    seoKeywords:
      "music catalog AI, AI soundalike detection, lyric moderation, cover art moderation, music copyright AI",
  },

  // ── Media & Sports ──
  {
    slug: "brands",
    category: "Media & Sports",
    title: "For Brands",
    short:
      "Track sponsorships, logo placements, and brand mentions across digital media.",
    headline: "Measure every second your brand is on screen",
    subheadline:
      "Zenstream watches broadcasts, streams, and social video for your logo, your products, and your context — then proves the ROI of every sponsorship.",
    perks: [
      {
        title: "Logo + product detection",
        body: "Frame-accurate placement detection across thousands of streams.",
      },
      {
        title: "Context scoring",
        body: "Was the placement next to a winning play, or buried in a commercial break? We tell you.",
      },
      {
        title: "Brand-safety alerts",
        body: "Get pinged the instant your logo appears next to brand-unsafe content.",
      },
    ],
    triggerExamples: [
      "Alert me if our logo appears in any stream tagged NSFW or political.",
      "Tally on-screen seconds of our shoe in last weekend's matches.",
      "Score every placement by audience reaction in chat.",
    ],
    seoKeywords:
      "brand monitoring AI, logo detection, sponsorship measurement, brand safety, on-screen brand tracking",
  },
  {
    slug: "publishers",
    category: "Media & Sports",
    title: "For Publishers",
    short: "Monitor and optimize advertising and sponsorship inventory.",
    headline: "Inventory intelligence for modern publishers",
    subheadline:
      "Know which scenes, articles, and clips are actually brand-safe — and which deserve premium CPM. Zenstream classifies your inventory automatically.",
    perks: [
      {
        title: "Frame-level safety",
        body: "Per-scene IAB taxonomy classification, not just per-video.",
      },
      {
        title: "Programmatic-ready signals",
        body: "Push classifications straight into your SSP for smarter targeting.",
      },
      {
        title: "Auto-clipping for sales",
        body: "Generate sponsor-friendly clips on demand with English prompts.",
      },
    ],
    triggerExamples: [
      "Tag every article whose hero image depicts a child.",
      "Flag video segments that mention a competitor by name.",
      "Build a 30-second sizzle reel of every goal scored this season.",
    ],
    seoKeywords:
      "publisher brand safety, ad inventory classification, IAB content classification, contextual advertising AI",
  },
  {
    slug: "agencies",
    category: "Media & Sports",
    title: "For Agencies",
    short:
      "Measure ad effectiveness and verify brand-safe placements across platforms.",
    headline: "One platform for every campaign you run",
    subheadline:
      "Verify that every placement actually ran, score brand-lift in real time, and pull receipts for clients without re-watching a single hour of tape.",
    perks: [
      {
        title: "Multi-platform reach",
        body: "YouTube, TikTok, Twitch, broadcast — one schema, one report.",
      },
      {
        title: "Receipt-grade verification",
        body: "Every claim a creator makes is backed by frame-stamped evidence.",
      },
      {
        title: "Plain-English KPIs",
        body: "\"Show me every second the product was visible during the unboxing.\"",
      },
    ],
    triggerExamples: [
      "Verify that every contracted creator showed the product on screen for 5+ seconds.",
      "Score each placement against the agreed brand-safety floor.",
      "Generate the post-campaign report, with timestamps, automatically.",
    ],
    seoKeywords:
      "agency brand safety, influencer verification, campaign measurement AI, sponsorship analytics",
  },
  {
    slug: "teams-leagues",
    category: "Media & Sports",
    title: "For Teams and Leagues",
    short: "Measure brand health efficiently and maximize sponsorship value.",
    headline: "Monetize every minute of your media rights",
    subheadline:
      "Zenstream measures sponsor exposure across every broadcast and social clip, then auto-generates the highlight reels your sponsors want.",
    perks: [
      {
        title: "Stadium-wide measurement",
        body: "Boards, jerseys, in-broadcast graphics — measured per-impression.",
      },
      {
        title: "Auto-highlight generator",
        body: "\"Make a 60-second hype reel of every save in the match.\" Done.",
      },
      {
        title: "Sponsor-ready exports",
        body: "Hand your partners receipts, not vague reach numbers.",
      },
    ],
    triggerExamples: [
      "Tally on-screen seconds of every sponsor logo for last night's broadcast.",
      "Build a 90-second hype reel from this week's wins.",
      "Alert me if any sponsor logo appears next to an injury timeout.",
    ],
    seoKeywords:
      "sports sponsorship analytics, league brand exposure, highlight reel AI, sports media rights",
  },

  // ── Risk & Identity Management ──
  {
    slug: "government-agencies",
    category: "Risk & Identity Management",
    title: "For Government Agencies",
    short:
      "Protect communications, detect financial crimes, and combat misinformation.",
    headline: "Mission-grade content intelligence",
    subheadline:
      "Identify foreign-influence operations, deepfaked media, and CSAM at scale, with auditable English-language policies your analysts can defend in court.",
    perks: [
      {
        title: "Deepfake detection",
        body: "Synthetic-media scoring on faces, voices, and full clips.",
      },
      {
        title: "Influence-op tracking",
        body: "Cluster narratives across platforms with multimodal embeddings.",
      },
      {
        title: "Defensible policy",
        body: "Every action ties back to a human-readable rule.",
      },
    ],
    triggerExamples: [
      "Flag clips whose audio shows hallmarks of voice cloning.",
      "Cluster posts that push the same narrative across platforms.",
      "Auto-redact any PII in incoming citizen submissions.",
    ],
    seoKeywords:
      "government content intelligence, deepfake detection, misinformation analysis, public sector AI moderation",
  },
  {
    slug: "insurance",
    category: "Risk & Identity Management",
    title: "For Insurance Companies",
    short:
      "Detect fraudulent claims, verify documents, and streamline operations.",
    headline: "Claims you can trust, faster",
    subheadline:
      "Score every claim photo, video, and document for tampering and inconsistency. Get straight-through processing on the 80% that are clean.",
    perks: [
      {
        title: "Photo/document tampering detection",
        body: "Identify edited, AI-generated, or recycled claim media.",
      },
      {
        title: "Damage estimation prompts",
        body: "\"Estimate hail damage from these roof photos.\" — returns ranges and confidence.",
      },
      {
        title: "Document OCR + verification",
        body: "Extract policy IDs, dates, and totals; flag mismatches.",
      },
    ],
    triggerExamples: [
      "Flag any claim photo whose metadata or visual signature looks edited.",
      "Match the license plate in the claim photo to the policy on file.",
      "Reject document uploads where the issue date is in the future.",
    ],
    seoKeywords:
      "insurance claims AI, fraud detection AI, document verification, claim photo analysis",
  },
  {
    slug: "financial-services",
    category: "Risk & Identity Management",
    title: "For Financial Services",
    short: "Verify user identities and enhance fraud prevention.",
    headline: "Identity, fraud, and content compliance in one stack",
    subheadline:
      "From KYC selfies to wire-confirmation calls, Zenstream applies the same multimodal engine to every customer touchpoint.",
    perks: [
      {
        title: "KYC selfie + document",
        body: "Liveness, document parsing, and face-match in a single call.",
      },
      {
        title: "Voice deepfake screening",
        body: "Detect synthetic voices on confirmation calls before money moves.",
      },
      {
        title: "Marketing compliance",
        body: "Pre-publish review of every social post against your regulator's rules.",
      },
    ],
    triggerExamples: [
      "Block KYC selfies that look photo-of-screen.",
      "Hold the wire if the confirmation voice scores high for synthesis.",
      "Reject any social post whose copy could be read as a guarantee of returns.",
    ],
    seoKeywords:
      "financial services KYC AI, fraud detection, voice deepfake detection, regulated marketing review",
  },

  // ── Use Cases ──
  {
    slug: "content-moderation",
    category: "Use Cases",
    title: "Content Moderation",
    short:
      "Protect your community with moderation of video, image, text, and audio.",
    headline: "One moderation engine for every modality",
    subheadline:
      "Stop bolting together five vendors. Zenstream classifies every piece of user content with the same English-language rules — and reasons about edge cases instead of failing on them.",
    perks: [
      {
        title: "Video, image, audio, text",
        body: "Single API, single billing, single audit log.",
      },
      {
        title: "Reasoning model under the hood",
        body: "Handles sarcasm, coded language, and context that regex moderation misses.",
      },
      {
        title: "Reviewer-friendly explanations",
        body: "Every decision comes with a sentence-long rationale.",
      },
    ],
    triggerExamples: [
      "Hide content that targets a user's appearance, race, or disability.",
      "Block uploads that depict glorification of violence.",
      "Quarantine borderline cases for a human reviewer with a reason attached.",
    ],
    seoKeywords:
      "AI content moderation, multimodal moderation, video moderation API, image moderation, audio moderation",
  },
  {
    slug: "custom-training-automl",
    category: "Use Cases",
    title: "Custom Training — AutoML",
    short: "Build and deploy deep learning models for custom use cases.",
    headline: "Train your own model in an afternoon",
    subheadline:
      "Upload examples, write the trigger in English, and Zenstream fine-tunes a model you can call from the same API — no ML team required.",
    perks: [
      {
        title: "Few-shot fine-tuning",
        body: "Get a working model from 50–500 labeled examples.",
      },
      {
        title: "English-as-spec",
        body: "Your trigger doubles as the spec for the model.",
      },
      {
        title: "Hot-swap deploys",
        body: "Push a new version to a tenant in seconds, with rollback.",
      },
    ],
    triggerExamples: [
      "Train a model that detects our specific product in user-submitted photos.",
      "Train a model that flags mentions of our internal codenames.",
      "Train a model that scores audio for our brand's tone-of-voice.",
    ],
    seoKeywords:
      "AutoML platform, custom AI moderation, fine-tune vision model, custom content classifier",
  },
  {
    slug: "moderation-review-tool",
    category: "Use Cases",
    title: "Moderation Review Tool",
    short:
      "Streamline moderation workflows with our no-code Moderation Dashboard.",
    headline: "A no-code dashboard your reviewers will actually use",
    subheadline:
      "Triage queues, keyboard-shortcut decisions, side-by-side context, and an audit trail — every action ties back to the natural-language trigger that surfaced it.",
    perks: [
      {
        title: "Keyboard-first triage",
        body: "Top-tier reviewers clear 4× the volume without a mouse.",
      },
      {
        title: "Reasoning shown inline",
        body: "Why was this flagged? Plain English, every time.",
      },
      {
        title: "Reviewer wellness",
        body: "Configurable blur and grayscale on graphic media; rotation reminders.",
      },
    ],
    triggerExamples: [
      "Show me everything our 'unsafe-medical-advice' trigger fired today.",
      "Bulk-approve all items that match the 'low-risk product photo' rule.",
      "Auto-route items containing minors to senior reviewers only.",
    ],
    seoKeywords:
      "moderation dashboard, content review workflow, trust and safety tool, moderation queue UI",
  },
  {
    slug: "sponsorship-intelligence",
    category: "Use Cases",
    title: "Sponsorship Intelligence",
    short:
      "Cross-platform sponsorship measurement powered by Zenstream's AI.",
    headline: "Sponsorship measurement that finally tells the truth",
    subheadline:
      "Stop trusting media kits. Zenstream measures real on-screen exposure across every channel and ties it to audience reaction.",
    perks: [
      {
        title: "Frame-accurate exposure",
        body: "Per-second logo and product visibility, not Nielsen-style estimates.",
      },
      {
        title: "Reaction-weighted value",
        body: "30 seconds during a goal is worth more than 30 seconds in a commercial — we score it.",
      },
      {
        title: "Cross-platform schema",
        body: "Compare a TV broadcast and a TikTok Live in the same units.",
      },
    ],
    triggerExamples: [
      "Measure on-screen seconds of our logo across every event this quarter.",
      "Compare our brand exposure vs. our top three competitors.",
      "Surface every clip where our product appeared during a high-engagement moment.",
    ],
    seoKeywords:
      "sponsorship measurement, brand exposure analytics, sponsorship ROI, sponsorship AI platform",
  },
  {
    slug: "ad-intelligence",
    category: "Use Cases",
    title: "Ad Intelligence",
    short: "Analyze cross-platform ad intelligence in our Mensio platform.",
    headline: "See every ad your competitors run — and score them",
    subheadline:
      "Zenstream watches broadcast, streaming, and social and structures every ad: brand, product, claim, creative tone. Run market research without an agency.",
    perks: [
      {
        title: "Cross-platform ad coverage",
        body: "Linear, CTV, YouTube, TikTok — normalized into one schema.",
      },
      {
        title: "Creative scoring",
        body: "Tone, emotion, clarity-of-claim, brand recall — graded by the same model.",
      },
      {
        title: "Plain-English search",
        body: "\"Show me every competitor ad that mentions speed.\"",
      },
    ],
    triggerExamples: [
      "Index every ad our competitors aired in the last 30 days.",
      "Score the emotional tone of each ad against the category benchmark.",
      "Alert me when a competitor launches a creative with a new claim.",
    ],
    seoKeywords:
      "ad intelligence platform, competitive ad tracking, creative analytics, broadcast ad monitoring",
  },
  {
    slug: "context-based-ad-targeting",
    category: "Use Cases",
    title: "Context-Based Ad Targeting",
    short:
      "Capitalize on ad inventory with contextual targeting and brand safety analysis.",
    headline: "Contextual targeting that beats cookies",
    subheadline:
      "Match ads to the *content* people are watching, not the data trail behind them. Zenstream classifies every scene with brand-safety and IAB taxonomy in one pass.",
    perks: [
      {
        title: "Per-scene targeting",
        body: "Place an athletic-shoe ad on the goal-replay scene, not the full match.",
      },
      {
        title: "Privacy-friendly by default",
        body: "No personal data — just what's actually on screen.",
      },
      {
        title: "Programmatic-grade APIs",
        body: "Sub-100 ms classifications fit in your bid path.",
      },
    ],
    triggerExamples: [
      "Place our ad on every scene tagged 'cooking' and brand-safe.",
      "Avoid placements that border on 'political commentary'.",
      "Bid up on scenes that score high for 'celebration'.",
    ],
    seoKeywords:
      "contextual advertising AI, scene-level targeting, IAB classification, cookieless ad targeting",
  },

  // ── API ──
  {
    slug: "static-image-api",
    category: "API",
    title: "Static Image Moderation API",
    short:
      "One endpoint to score, classify, and gate any static image — written in English.",
    headline: "Static Image Moderation API",
    subheadline:
      "POST an image, send a natural-language trigger like \"Reject if this contains a firearm or any depiction of a minor,\" get a structured decision in milliseconds. The same engine the rest of Zenstream is built on — exposed as a single endpoint.",
    perks: [
      {
        title: "Plain-English triggers",
        body: "No taxonomies to learn. Write the rule, ship it, iterate in production.",
      },
      {
        title: "Multimodal reasoning, image-only price",
        body: "Pay for image moderation only — but get a model that reasons about context, not just pixels.",
      },
      {
        title: "Structured, explainable output",
        body: "Every decision returns the trigger that matched, a confidence score, and a sentence-long rationale.",
      },
      {
        title: "Sub-300 ms latency",
        body: "Fits inside upload pipelines, marketplaces, and sign-up flows.",
      },
    ],
    triggerExamples: [
      "Reject any image containing a firearm, even partially obscured.",
      "Block uploads that depict minors in any context.",
      "Approve only photos that look like authentic phone-camera captures, not screenshots.",
      "Tag images that look like they were generated by an AI image model.",
    ],
    seoKeywords:
      "image moderation API, static image AI, NSFW image API, image classification API, image moderation natural language, image moderation with prompts",
    isApi: true,
  },
];

export function getSolution(slug) {
  return SOLUTIONS.find((s) => s.slug === slug);
}

export function getSolutionsByCategory() {
  return SOLUTION_CATEGORIES.map((category) => ({
    category,
    items: SOLUTIONS.filter((s) => s.category === category),
  }));
}
